import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Link,
  Divider,
} from "@mui/material";
import Box from "themeComponents/Box";
import Typography from "themeComponents/Typography";
import Button from "themeComponents/Button";
import Loader from "components/loader";
import SupportFileUpload from "./SupportFileUpload";
import useAuth from "hooks/useAuth";
import {
  SupportAttachmentInput,
  SupportComment,
  SupportTicket,
  SUPPORT_TICKET_STATUSES,
  formatTicketStatus,
} from "types/support";

interface TicketDetailDialogProps {
  open: boolean;
  ticketId: string | null;
  onClose: () => void;
  onRefreshList: () => void;
  getTicket: (id: string) => Promise<SupportTicket | null>;
  listComments: (ticketId: string) => Promise<SupportComment[]>;
  addComment: (
    ticketId: string,
    body: string,
    attachments?: SupportAttachmentInput[]
  ) => Promise<SupportComment>;
  updateStatus: (id: string, status: string) => Promise<SupportTicket>;
  escalateTicket: (id: string) => Promise<SupportTicket>;
  onUpload: (file: File) => Promise<SupportAttachmentInput>;
}

function TicketDetailDialog({
  open,
  ticketId,
  onClose,
  onRefreshList,
  getTicket,
  listComments,
  addComment,
  updateStatus,
  escalateTicket,
  onUpload,
}: TicketDetailDialogProps): React.JSX.Element {
  const { isAdmin } = useAuth();
  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [comments, setComments] = useState<SupportComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [commentBody, setCommentBody] = useState("");
  const [commentAttachments, setCommentAttachments] = useState<SupportAttachmentInput[]>([]);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [statusValue, setStatusValue] = useState("open");

  const load = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const [t, c] = await Promise.all([getTicket(ticketId), listComments(ticketId)]);
      setTicket(t);
      setComments(c);
      if (t) setStatusValue(t.status);
    } finally {
      setLoading(false);
    }
  }, [ticketId, getTicket, listComments]);

  useEffect(() => {
    if (open && ticketId) {
      void load();
    } else {
      setTicket(null);
      setComments([]);
      setCommentBody("");
      setCommentAttachments([]);
    }
  }, [open, ticketId, load]);

  const handleStatusChange = async (newStatus: string) => {
    if (!ticketId || !ticket) return;
    setStatusValue(newStatus);
    try {
      const updated = await updateStatus(ticketId, newStatus);
      setTicket(updated);
      onRefreshList();
    } catch {
      setStatusValue(ticket.status);
    }
  };

  const handleEscalate = async () => {
    if (!ticketId) return;
    setEscalating(true);
    try {
      const updated = await escalateTicket(ticketId);
      setTicket(updated);
      onRefreshList();
    } finally {
      setEscalating(false);
    }
  };

  const handleAddComment = async () => {
    if (!ticketId || !commentBody.trim()) return;
    setSubmittingComment(true);
    try {
      const created = await addComment(
        ticketId,
        commentBody.trim(),
        commentAttachments.length > 0 ? commentAttachments : undefined
      );
      setComments((prev) => [...prev, created]);
      setCommentBody("");
      setCommentAttachments([]);
      onRefreshList();
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatDate = (value: string) => {
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableEnforceFocus
      disableRestoreFocus
      data-testid="support-ticket-detail-dialog"
    >
      <DialogTitle>{ticket?.subject || "Ticket details"}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Loader loading={true} {...({} as any)} />
          </Box>
        ) : ticket ? (
          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
              {isAdmin || ticket.canChangeStatus ? (
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    data-testid="support-status-select"
                    label="Status"
                    value={statusValue}
                    onChange={(e) => void handleStatusChange(e.target.value)}
                    disabled={!ticket.canChangeStatus}
                  >
                    {SUPPORT_TICKET_STATUSES.map((s) => (
                      <MenuItem key={s.value} value={s.value}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography variant="button" color="info">
                  {formatTicketStatus(ticket.status)}
                </Typography>
              )}
              <Typography variant="caption" color="text">
                Created {formatDate(ticket.createdAt)} by{" "}
                {ticket.createdByDisplayName || ticket.createdByUserId}
              </Typography>
            </Box>

            {ticket.canEscalate ? (
              <Button
                data-testid="support-escalate-button"
                color="warning"
                variant="outlined"
                size="small"
                loading={escalating}
                onClick={() => void handleEscalate()}
                sx={{ alignSelf: "flex-start" }}
              >
                Send to admins
              </Button>
            ) : null}

            <Box>
              <Typography variant="button" fontWeight="bold" mb={0.5}>
                Message
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {ticket.message}
              </Typography>
            </Box>

            {ticket.attachments && ticket.attachments.length > 0 ? (
              <Box>
                <Typography variant="button" fontWeight="bold" mb={0.5}>
                  Attachments
                </Typography>
                {ticket.attachments.map((att) => (
                  <Box key={att.fileUrl}>
                    <Link href={att.fileUrl} target="_blank" rel="noopener noreferrer">
                      {att.fileName || att.fileUrl}
                    </Link>
                  </Box>
                ))}
              </Box>
            ) : null}

            <Divider />

            <Box>
              <label
                htmlFor="support-comment-body"
                style={{ display: "block", marginBottom: 4, cursor: "pointer" }}
              >
                <Typography variant="caption" fontWeight="bold">
                  Comment
                </Typography>
              </label>
              <textarea
                id="support-comment-body"
                data-testid="support-comment-body"
                value={commentBody}
                onChange={(e) => setCommentBody(e.target.value)}
                placeholder="Write a comment..."
                rows={4}
                style={{
                  width: "100%",
                  minHeight: 96,
                  padding: 12,
                  borderRadius: 8,
                  border: "1px solid #ccc",
                  fontFamily: "inherit",
                  fontSize: 14,
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />
              <Box mt={1}>
                <SupportFileUpload
                  label="Attachments"
                  attachments={commentAttachments}
                  onRemove={(index) =>
                    setCommentAttachments((prev) => prev.filter((_, i) => i !== index))
                  }
                  onUpload={async (file) => {
                    const uploaded = await onUpload(file);
                    setCommentAttachments((prev) => [...prev, uploaded]);
                    return uploaded;
                  }}
                  disabled={submittingComment}
                />
              </Box>
              <Box mt={1.5}>
                <Button
                  type="button"
                  color="info"
                  variant="gradient"
                  size="small"
                  loading={submittingComment}
                  disabled={!commentBody.trim()}
                  onClick={() => void handleAddComment()}
                  data-testid="support-add-comment"
                >
                  Add comment
                </Button>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="button" fontWeight="bold" mb={1}>
                Comments
              </Typography>
              {comments.length === 0 ? (
                <Typography variant="body2" color="text">
                  No comments yet.
                </Typography>
              ) : (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  {comments.map((comment) => (
                    <Box
                      key={comment.id}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: comment.isOwnComment ? "action.selected" : "grey.100",
                      }}
                    >
                      <Typography variant="caption" color="text" display="block" mb={0.5}>
                        {comment.createdByDisplayName || comment.createdByUserId} ·{" "}
                        {formatDate(comment.createdAt)}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {comment.body}
                      </Typography>
                      {comment.attachments?.map((att) => (
                        <Box key={att.fileUrl} mt={0.5}>
                          <Link href={att.fileUrl} target="_blank" rel="noopener noreferrer" variant="caption">
                            {att.fileName || att.fileUrl}
                          </Link>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <Typography color="text" data-testid="support-ticket-not-found">
            Ticket not found.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button type="button" color="secondary" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TicketDetailDialog;
